<template>
  <div class="calendar-date-picker">
    <!-- 月選択ヘッダー -->
    <div class="calendar-header">
      <button 
        class="calendar-nav-btn" 
        @click="previousMonth"
        :disabled="isPreviousDisabled"
      >
        <i class="fas fa-chevron-left"></i>
      </button>
      <h3 class="calendar-title">{{ year }}年{{ month }}月</h3>
      <button 
        class="calendar-nav-btn" 
        @click="nextMonth"
        :disabled="isNextDisabled"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- カレンダー本体 -->
    <div class="calendar-wrapper">
      <!-- 曜日ヘッダー -->
      <div class="calendar-weekdays">
        <div 
          v-for="weekday in weekdays" 
          :key="weekday"
          class="calendar-weekday"
        >
          {{ weekday }}
        </div>
      </div>

      <!-- 日付グリッド -->
      <div class="calendar-days">
        <!-- 前月の空白日 -->
        <div 
          v-for="blank in firstDayOfWeek" 
          :key="`blank-${blank}`"
          class="calendar-day-blank"
        ></div>

        <!-- 当月の日付 -->
        <div
          v-for="day in daysInMonth"
          :key="day"
          class="calendar-day"
          :class="{
            'is-selected': isSelected(day),
            'is-disabled': isDisabled(day),
            'is-today': isToday(day)
          }"
          @click="selectDate(day)"
        >
          <span class="calendar-day-number">{{ day }}</span>
          <span 
            v-if="availability[day]" 
            class="calendar-day-status"
            :class="getStatusClass(availability[day])"
          >
            {{ availability[day] }}
          </span>
        </div>
      </div>
    </div>

    <!-- ローディング表示 -->
    <div v-if="isLoading" class="calendar-loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>空き状況を確認中...</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CalendarDatePicker',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    reservationType: {
      type: String,
      default: 'レンタル'
    }
  },
  data() {
    return {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      selectedDate: '',
      availability: {},
      isLoading: false,
      weekdays: ['日', '月', '火', '水', '木', '金', '土']
    };
  },
  computed: {
    // 月の日数
    daysInMonth() {
      return new Date(this.year, this.month, 0).getDate();
    },
    // 月初日の曜日（0: 日曜日）
    firstDayOfWeek() {
      return new Date(this.year, this.month - 1, 1).getDay();
    },
    // 前月ボタンの無効化判定
    isPreviousDisabled() {
      const today = new Date();
      const currentMonth = new Date(this.year, this.month - 1);
      const thisMonth = new Date(today.getFullYear(), today.getMonth());
      return currentMonth <= thisMonth;
    },
    // 次月ボタンの無効化判定
    isNextDisabled() {
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 6);
      const currentMonth = new Date(this.year, this.month - 1);
      return currentMonth >= new Date(maxDate.getFullYear(), maxDate.getMonth());
    }
  },
  mounted() {
    this.fetchMonthAvailability();
  },
  methods: {
    // 月間空き状況を取得
    async fetchMonthAvailability() {
      this.isLoading = true;
      try {
        const endpoint = process.env.NODE_ENV === 'production'
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/getMonthAvailability'
          : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/getMonthAvailability';

        // 見学の場合は10:00-11:00の枠のみチェック
        const params = {
          year: this.year,
          month: this.month
        };
        
        if (this.reservationType === '見学') {
          params.duration = 60;  // 1時間
          params.checkOnly10am = true;  // 10:00のみチェック
        }

        const response = await axios.get(endpoint, {
          params: params
        });

        if (response.data.success) {
          this.availability = response.data.availability;
        }
      } catch (error) {
        console.error('Error fetching month availability:', error);
      } finally {
        this.isLoading = false;
      }
    },
    // 前月へ
    previousMonth() {
      if (this.month === 1) {
        this.year--;
        this.month = 12;
      } else {
        this.month--;
      }
      this.fetchMonthAvailability();
    },
    // 次月へ
    nextMonth() {
      if (this.month === 12) {
        this.year++;
        this.month = 1;
      } else {
        this.month++;
      }
      this.fetchMonthAvailability();
    },
    // 日付選択
    selectDate(day) {
      if (this.isDisabled(day)) return;
      
      const dateString = `${this.year}-${String(this.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      this.selectedDate = dateString;
      this.$emit('update:modelValue', dateString);
    },
    // 選択状態の判定
    isSelected(day) {
      const dateString = `${this.year}-${String(this.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return this.selectedDate === dateString || this.modelValue === dateString;
    },
    // 無効状態の判定
    isDisabled(day) {
      const date = new Date(this.year, this.month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // 過去の日付または満席の日は選択不可
      return date <= today || this.availability[day] === '✕';
    },
    // 今日の判定
    isToday(day) {
      const today = new Date();
      return this.year === today.getFullYear() && 
             this.month === today.getMonth() + 1 && 
             day === today.getDate();
    },
    // ステータスクラスを取得
    getStatusClass(status) {
      switch(status) {
        case '◯': return 'status-available';
        case '△': return 'status-partial';
        case '✕': return 'status-full';
        default: return '';
      }
    }
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        const date = new Date(newVal);
        this.year = date.getFullYear();
        this.month = date.getMonth() + 1;
        this.selectedDate = newVal;
      }
    },
    reservationType() {
      // 予約タイプが変わったら空き状況を再取得
      this.fetchMonthAvailability();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.calendar-date-picker {
  margin: 0 auto;
  background: $white;
  border-radius: $radius-lg;
  box-shadow: $shadow-sm;
  overflow: hidden;
}

// ヘッダー
.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing-lg;
  background: $primary-color;
  color: $white;
}

.calendar-nav-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba($white, 0.3);
  border-radius: $radius-md;
  color: $white;
  cursor: pointer;
  transition: all $transition-base;
  
  &:hover:not(:disabled) {
    background: rgba($white, 0.1);
    border-color: rgba($white, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.calendar-title {
  font-size: $font-size-lg;
  font-weight: 600;
  margin: 0;
  color: $white;
}

// カレンダー本体
.calendar-wrapper {
  padding: $spacing-md;
  position: relative;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: $spacing-sm;
}

.calendar-weekday {
  text-align: center;
  font-size: $font-size-sm;
  font-weight: 600;
  color: $gray-600;
  padding: $spacing-xs;
  
  &:first-child {
    color: $danger;
  }
  
  &:last-child {
    color: $primary-color;
  }
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: $spacing-xs;
}

.calendar-day-blank {
  aspect-ratio: 1;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid $gray-200;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all $transition-base;
  position: relative;
  
  &:hover:not(.is-disabled) {
    background: $gray-100;
    border-color: $primary-color;
  }
  
  &.is-selected {
    background: $primary-color;
    color: $white;
    border-color: $primary-color;
    
    .calendar-day-status {
      color: $white;
    }
  }
  
  &.is-disabled {
    background: $gray-100;
    color: $gray-400;
    cursor: not-allowed;
    
    .calendar-day-status {
      opacity: 0.5;
    }
  }
  
  &.is-today {
    border-color: $primary-color;
    border-width: 2px;
  }
}

.calendar-day-number {
  font-size: $font-size-md;
  font-weight: 500;
  margin-bottom: $spacing-xs;
}

.calendar-day-status {
  font-size: $font-size-xs;
  font-weight: 600;
  
  &.status-available {
    color: $success;
  }
  
  &.status-partial {
    color: $warning;
  }
  
  &.status-full {
    color: $danger;
  }
}

// ローディング
.calendar-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba($white, 0.9);
  color: $gray-600;
  
  i {
    font-size: $font-size-2xl;
    margin-bottom: $spacing-sm;
  }
}

// レスポンシブ
@include media-breakpoint-down(sm) {
  .calendar-date-picker {
    max-width: 100%;
  }
  
  .calendar-header {
    padding: $spacing-md;
  }
  
  .calendar-nav-btn {
    width: 32px;
    height: 32px;
    font-size: $font-size-sm;
  }
  
  .calendar-title {
    font-size: $font-size-md;
  }
  
  .calendar-wrapper {
    padding: $spacing-sm;
  }
  
  .calendar-weekdays {
    margin-bottom: $spacing-xs;
  }
  
  .calendar-weekday {
    font-size: $font-size-xs;
    padding: $spacing-xs 0;
  }
  
  .calendar-days {
    gap: 2px;
  }
  
  .calendar-day {
    border-radius: $radius-sm;
    min-height: 40px;
    padding: $spacing-xs;
    
    &.is-today {
      border-width: 1px;
    }
  }
  
  .calendar-day-number {
    font-size: $font-size-sm;
    margin-bottom: 2px;
  }
  
  .calendar-day-status {
    font-size: 10px;
  }
}
</style>